import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, Suspense } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getServiceById } from "@/data/services";
import { useToast } from "@/hooks/use-toast";
import { Header } from "@/components/Header";
import { ServiceDetailsHeader } from "@/components/service-details/ServiceDetailsHeader";
import { ServiceInfo } from "@/components/service-details/ServiceInfo";
import { BookingSection } from "@/components/service-details/BookingSection";
import { ContactInfo } from "@/components/service-details/ContactInfo";
import { ReviewSection } from "@/components/service-details/ReviewSection";
import { Loading } from "@/components/service-details/Loading";
import { NotFound } from "@/components/service-details/NotFound";
import { useServicePermission } from "@/hooks/useServicePermission";
import { useIsMobile } from "@/hooks/use-mobile";
import { ServiceImageLoader } from "@/components/ServiceImageLoader";
import { PerformanceMonitor } from "@/components/PerformanceMonitor";
import { isSlowConnection } from "@/utils/networkMonitor";
import { ServiceDetailsLoader } from "@/components/service-details/ServiceDetailsLoader";
import { FavoriteButton } from "@/components/service-details/FavoriteButton";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { QUERY_VERSION } from "@/constants/serviceQueryConstants";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { 
  generateTransformationUrl, 
  getImageTransformOptions, 
  getDebugImageInfo 
} from "@/utils/imageTransformationUtils";

// Create separate lazy-loaded components for better code splitting
const LazyReviewSection = ({ serviceId }: { serviceId: number }) => {
  return (
    <Suspense fallback={<div className="p-4 bg-gray-50 rounded-lg mt-4"><div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div></div>}>
      <ReviewSection serviceId={serviceId} />
    </Suspense>
  );
};

const ServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const slowConnection = isSlowConnection();
  
  const { isAdmin, isOwner, canEdit } = useServicePermission(id);
  
  // Show debug info in development and preview environments
  const isDev = window.location.hostname.includes('localhost') || 
                window.location.hostname.includes('127.0.0.1');
  const isPreview = window.location.hostname.includes('preview') || 
                   window.location.hostname.includes('lovable.app');
  const showDebugInfo = isDev || isPreview;

  // Force refetch reviews when component mounts
  useEffect(() => {
    if (id) {
      const serviceId = parseInt(id, 10);
      // Only invalidate on faster connections to reduce data usage
      if (!slowConnection) {
        queryClient.invalidateQueries({ queryKey: ['reviews', serviceId] });
        console.log('ServiceDetails mounted, invalidating reviews cache for serviceId:', serviceId);
      }
    }
  }, [id, queryClient, slowConnection]);

  const { data: service, isLoading, error } = useQuery({
    queryKey: ['service', id, QUERY_VERSION],
    queryFn: async () => {
      if (!id) return null;
      try {
        const serviceId = parseInt(id, 10);
        return await getServiceById(serviceId);
      } catch (error) {
        console.error('Error loading service:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load service details"
        });
        throw error;
      }
    },
    staleTime: isMobile || slowConnection ? 30 * 60 * 1000 : 5 * 60 * 1000, // 30 min for mobile/slow, 5 min otherwise
    gcTime: isMobile || slowConnection ? 60 * 60 * 1000 : 15 * 60 * 1000,   // 1 hour for mobile/slow, 15 min otherwise
    refetchOnWindowFocus: !isMobile && !slowConnection, // Disable refetch for mobile or slow connections
  });

  // Helper function to get image data - Enhanced with safety checks
  const getImageData = () => {
    if (!service) return { src: '', variants: {}, placeholder: null, debugInfo: '' };
    
    // Default fallback image
    const fallbackSrc = 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b';
    let src = '';
    let originalSrc = '';
    let variants: { main: string; preview: string } = { main: '', preview: '' };
    let debugInfo = '';
    
    try {
      // Try to get optimized image data from the relational structure first
      const serviceImages = service.service_images;
      if (serviceImages && Array.isArray(serviceImages) && serviceImages.length > 0) {
        console.log(`Found ${serviceImages.length} images for service ${service.id}`);
        
        // Try to find the index image first
        const indexImage = serviceImages.find(img => img?.is_index_image === true);
        const firstImage = serviceImages.length > 0 ? serviceImages[0] : null;
        const selectedImage = indexImage || firstImage;
        
        if (selectedImage?.original_path) {
          try {
            const { data } = supabase.storage.from('services').getPublicUrl(selectedImage.original_path);
            if (data?.publicUrl) {
              src = data.publicUrl;
              originalSrc = src;
              console.log('Using image from storage:', src);
              
              // Get transform options based on device and network
              const transformOptions = getImageTransformOptions(
                'detail', 
                isMobile, 
                isSlowConnection()
              );
              
              // Generate variants using our utility function
              variants = {
                main: generateTransformationUrl(src, {
                  width: transformOptions.width,
                  quality: transformOptions.quality,
                  resize: 'cover'
                }),
                preview: generateTransformationUrl(src, {
                  width: Math.floor(transformOptions.width / 4),
                  quality: 40,
                  resize: 'cover'
                })
              };
              
              // Set debug info for development/preview environments
              if (showDebugInfo) {
                debugInfo = getDebugImageInfo(src, variants.main);
              }
              
              console.log('Created dynamic transformation variants', variants);
            }
          } catch (error) {
            console.error('Error getting public URL:', error);
            // Set safe debug info in case of error
            debugInfo = 'Error getting public URL';
          }
        }
      }
      
      // If we still don't have a source, try using image_url before falling back
      if (!src && service.image_url) {
        src = service.image_url;
        originalSrc = src;
        console.log('Using image_url as fallback:', src);
        
        // Get transform options based on device and network
        const transformOptions = getImageTransformOptions(
          'detail', 
          isMobile, 
          isSlowConnection()
        );
        
        // Generate variants using our utility function
        variants = {
          main: generateTransformationUrl(src, {
            width: transformOptions.width,
            quality: transformOptions.quality,
            resize: 'cover'
          }),
          preview: generateTransformationUrl(src, {
            width: Math.floor(transformOptions.width / 4),
            quality: 40,
            resize: 'cover'
          })
        };
      }
      
      // If we still don't have a source, use fallback
      if (!src) {
        src = fallbackSrc;
        originalSrc = fallbackSrc;
        console.log('No image found, using fallback');
      }
    } catch (error) {
      console.error('Unexpected error in getImageData:', error);
      src = fallbackSrc;
      originalSrc = fallbackSrc;
      debugInfo = 'Error processing image data';
    }
    
    // Final safety check for debugInfo
    if (typeof debugInfo !== 'string') {
      debugInfo = 'Invalid debug info format';
    }
    
    return { 
      src, 
      originalSrc,
      variants, 
      placeholder: null, // We don't have placeholders in the database
      debugInfo
    };
  };

  const handleImageError = () => {
    console.log('Image failed to load, setting error state');
    setImageError(true);
    setImageLoaded(false);
    
    toast({
      variant: "destructive",
      title: "Image Error",
      description: "Failed to load service image"
    });
  };

  const handleImageLoad = () => {
    console.log('Image loaded successfully');
    setImageLoaded(true);
    setImageError(false);
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error || !service) {
    return <NotFound navigate={navigate} />;
  }

  const { src, originalSrc, variants, placeholder, debugInfo } = getImageData();
  
  // For development purposes, add more detailed logging about image data
  console.log('Final image data for service', service.id, ':', { 
    src, 
    originalSrc,
    variantCount: Object.keys(variants).length, 
    hasPlaceholder: !!placeholder,
    serviceHasImages: service.images && service.images.length > 0,
    imageError,
    debugInfo
  });

  return (
    <ErrorBoundary componentName="ServiceDetails">
      <ServiceDetailsLoader>
        <Header />
        <PerformanceMonitor />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 md:pt-20">
          <div className="container py-1 px-1 md:px-4 md:py-4">
            <ServiceDetailsHeader 
              navigate={navigate} 
              id={id} 
              service={service} 
              isAdmin={isAdmin} 
              isServiceOwner={isOwner}
              canEdit={canEdit}
            />

            {/* Enhanced two-column layout for main service info */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md mb-6">
              {/* Mobile display - stacked layout */}
              <div className="md:hidden">
                <div className="aspect-[16/9] relative">
                  <ServiceImageLoader
                    src={src}
                    alt={service.name || 'Service image'}
                    aspectRatio={16/9}
                    priority={true}
                    sizes="100vw"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    variants={Object.keys(variants).length > 0 ? variants : undefined}
                    placeholder={placeholder}
                    className="transition-all duration-300 hover:scale-105"
                    fallbackSrc="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b"
                    showPlaceholder={!imageLoaded || imageError}
                    debugInfo={showDebugInfo ? debugInfo : undefined}
                  />
                </div>
                
                <div className="p-3">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h1 className="text-2xl font-bold text-gray-900">{service.name}</h1>
                    </div>
                    <FavoriteButton serviceId={service.id} />
                  </div>
                  <ServiceInfo service={service} />
                  <ContactInfo service={service} />
                </div>
              </div>
              
              {/* Desktop display - improved side by side layout */}
              <div className="hidden md:flex flex-row">
                <div className="w-1/2 p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h1 className="text-3xl font-bold text-gray-900">{service.name}</h1>
                    </div>
                    <FavoriteButton serviceId={service.id} />
                  </div>
                  <ServiceInfo service={service} />
                  <ContactInfo service={service} />
                </div>
                
                <div className="w-1/2 relative p-6">
                  <div className="h-full overflow-hidden rounded-xl border-[6px] border-primary-light/30 shadow-lg">
                    <ServiceImageLoader
                      src={src}
                      alt={service.name || 'Service image'}
                      aspectRatio={1}
                      priority={true}
                      sizes="50vw"
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                      variants={Object.keys(variants).length > 0 ? variants : undefined}
                      placeholder={placeholder}
                      fallbackSrc="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b"
                      className="h-full w-full object-cover"
                      showPlaceholder={!imageLoaded}
                      debugInfo={showDebugInfo ? debugInfo : undefined}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Full width section for booking and other content below */}
            <Card className="p-4 mb-6 bg-white shadow-md">
              {service.calendar && <BookingSection service={service} />}
            </Card>
            
            {/* Reviews section - full width */}
            <Card className="p-4 mb-6 bg-white shadow-md">
              {id && parseInt(id, 10) > 0 && (
                <LazyReviewSection serviceId={parseInt(id, 10)} />
              )}
            </Card>
          </div>
        </div>
      </ServiceDetailsLoader>
    </ErrorBoundary>
  );
};

export default ServiceDetails;
